import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { inrPlain, inr, todayIso, ddmmyyyy } from "@/lib/format";
import { FileXls, FilePdf, FileCsv, DownloadSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBooks } from "@/context/BookContext";

const downloadStatement = async ({ name, mode, date_from, date_to, book_id }, fmt) => {
  const params = new URLSearchParams({ format: fmt });
  if (name) params.set("name", name);
  if (mode) params.set("mode", mode);
  if (date_from) params.set("date_from", date_from);
  if (date_to) params.set("date_to", date_to);
  // Prefer explicit book_id; else use the active book from context (via localStorage)
  const bId = book_id || localStorage.getItem("jespay_active_book") || "";
  if (bId) params.set("book_id", bId);
  try {
    const res = await fetch(`${API}/transactions/export?${params.toString()}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error("export failed");
    const blob = await res.blob();
    // Grab filename from Content-Disposition
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename=([^;]+)/);
    const filename = m ? m[1].trim().replace(/^"|"$/g, "") : `jespay_statement.${fmt}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed");
  }
};

const FormatButtons = ({ filter, testidPrefix }) => (
  <div className="flex gap-1">
    <button
      data-testid={`${testidPrefix}-xlsx`}
      onClick={() => downloadStatement(filter, "xlsx")}
      title="Download Excel"
      className="brut-btn brut-btn-secondary p-2"
    >
      <FileXls size={14} weight="bold" color="#16a063" />
    </button>
    <button
      data-testid={`${testidPrefix}-pdf`}
      onClick={() => downloadStatement(filter, "pdf")}
      title="Download PDF"
      className="brut-btn brut-btn-secondary p-2"
    >
      <FilePdf size={14} weight="bold" color="#c53030" />
    </button>
    <button
      data-testid={`${testidPrefix}-csv`}
      onClick={() => downloadStatement(filter, "csv")}
      title="Download CSV"
      className="brut-btn brut-btn-secondary p-2"
    >
      <FileCsv size={14} weight="bold" color="#1e2a6b" />
    </button>
  </div>
);

export default function Reports() {
  const { activeId, activeBook } = useBooks();
  const [byPerson, setByPerson] = useState([]);
  const [byBank, setByBank] = useState([]);
  const [builder, setBuilder] = useState({
    name: "",
    mode: "",
    date_from: "",
    date_to: todayIso(),
  });
  const [preview, setPreview] = useState(null); // null | {rows, totals}
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const params = activeId ? { book_id: activeId } : {};
      const [a, b] = await Promise.all([
        api.get("/summary/by-person", { params }),
        api.get("/summary/by-bank", { params }),
      ]);
      setByPerson(a.data);
      setByBank(b.data);
    })();
  }, [activeId]);

  const filterWithBook = (extra = {}) => ({ ...extra, book_id: activeId || undefined });

  const applyPreview = async () => {
    setPreviewLoading(true);
    try {
      const params = {};
      if (activeId) params.book_id = activeId;
      if (builder.name) params.name = builder.name;
      if (builder.mode) params.mode = builder.mode;
      if (builder.date_from) params.date_from = builder.date_from;
      if (builder.date_to) params.date_to = builder.date_to;
      const { data } = await api.get("/transactions", { params });
      const totalIn = data.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0);
      const totalOut = data.filter(r => r.amount < 0).reduce((s, r) => s + r.amount, 0);
      setPreview({
        rows: data,
        totals: { in: totalIn, out: totalOut, net: totalIn + totalOut, count: data.length },
      });
    } catch (err) {
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6]">Analytics · Statements{activeBook ? ` · ${activeBook.name}` : ""}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">Reports.</h1>
        </div>

      {/* Statement builder */}
      <div className="brut-card p-5" data-testid="statement-builder">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="brut-label">Custom statement</div>
            <div className="font-display font-black text-2xl">Build & download</div>
          </div>
          <div className="text-xs text-[#4a5578]">Filter by name / mode / dates, then export.</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="brut-label block mb-2">Person / Name</label>
            <select
              data-testid="builder-name"
              value={builder.name}
              onChange={(e) => setBuilder({ ...builder, name: e.target.value })}
              className="brut-input"
            >
              <option value="">— All people —</option>
              {byPerson.map((p) => (
                <option key={p.name} value={p.name}>{p.name} ({p.count})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="brut-label block mb-2">Mode</label>
            <select
              data-testid="builder-mode"
              value={builder.mode}
              onChange={(e) => setBuilder({ ...builder, mode: e.target.value })}
              className="brut-input"
            >
              <option value="">All modes</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="dtt">DTT</option>
            </select>
          </div>
          <div>
            <label className="brut-label block mb-2">From date</label>
            <input
              data-testid="builder-from"
              type="date"
              value={builder.date_from}
              onChange={(e) => setBuilder({ ...builder, date_from: e.target.value })}
              className="brut-input"
            />
          </div>
          <div>
            <label className="brut-label block mb-2">To date</label>
            <input
              data-testid="builder-to"
              type="date"
              value={builder.date_to}
              onChange={(e) => setBuilder({ ...builder, date_to: e.target.value })}
              className="brut-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={applyPreview}
            disabled={previewLoading}
            className="brut-btn brut-btn-teal flex items-center gap-2"
            data-testid="builder-apply"
          >
            <Eye size={16} weight="bold" /> {previewLoading ? "Loading…" : "Apply & preview"}
          </button>
          <button
            onClick={() => downloadStatement(builder, "xlsx")}
            className="brut-btn brut-btn-primary flex items-center gap-2"
            data-testid="builder-download-xlsx"
          >
            <FileXls size={16} weight="bold" /> Excel
          </button>
          <button
            onClick={() => downloadStatement(builder, "pdf")}
            className="brut-btn brut-btn-secondary flex items-center gap-2"
            data-testid="builder-download-pdf"
          >
            <FilePdf size={16} weight="bold" /> PDF
          </button>
          <button
            onClick={() => downloadStatement(builder, "csv")}
            className="brut-btn brut-btn-secondary flex items-center gap-2"
            data-testid="builder-download-csv"
          >
            <FileCsv size={16} weight="bold" /> CSV
          </button>
          <button
            onClick={() => { setBuilder({ name: "", mode: "", date_from: "", date_to: "" }); setPreview(null); }}
            className="brut-btn brut-btn-secondary text-xs"
            data-testid="builder-reset"
          >
            Reset filters
          </button>
        </div>
      </div>

      {preview && (
        <div className="brut-card p-0 overflow-hidden" data-testid="builder-preview">
          <div className="p-5 border-b-2 border-[#0f1b4c] flex flex-wrap justify-between gap-3">
            <div>
              <div className="brut-label">Preview</div>
              <div className="font-display font-black text-2xl">
                {preview.totals.count} entries
                {builder.name ? ` · ${builder.name}` : ""}
                {builder.mode ? ` · ${builder.mode.toUpperCase()}` : ""}
              </div>
            </div>
            <div className="flex gap-6 items-end">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4a5578]">Inflow</div>
                <div className="font-mono-fin font-bold text-lg num-pos">{inrPlain(preview.totals.in)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4a5578]">Outflow</div>
                <div className="font-mono-fin font-bold text-lg num-neg">{inrPlain(Math.abs(preview.totals.out))}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4a5578]">Net</div>
                <div className={`font-mono-fin font-bold text-lg ${preview.totals.net < 0 ? "num-neg" : "num-pos"}`}>{inr(preview.totals.net)}</div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="brut" data-testid="preview-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Name</th>
                  <th className="text-right">Amount</th>
                  <th>Bank</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono-fin">{ddmmyyyy(r.date)}</td>
                    <td>
                      <span className={`chip ${r.mode === "cash" ? "chip-cash" : r.mode === "upi" ? "chip-upi" : "chip-dtt"}`}>{r.mode}</span>
                    </td>
                    <td className="font-bold">{r.name}</td>
                    <td className={`text-right font-mono-fin ${r.amount < 0 ? "num-neg" : "num-pos"}`}>{inr(r.amount)}</td>
                    <td className="text-[#4a5578]">{r.bank || "—"}</td>
                    <td className="text-[#4a5578]">{r.purpose || "—"}</td>
                  </tr>
                ))}
                {preview.rows.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-[#4a5578]">No transactions match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="brut-card p-0 overflow-hidden">
          <div className="p-5 border-b-2 border-[#0f1b4c]">
            <div className="flex justify-between items-center">
              <div>
                <div className="brut-label">Person-wise statements</div>
                <div className="font-display font-black text-2xl">By Name / Category</div>
              </div>
              <div className="text-xs text-[#4a5578]">Click a format to download that person's ledger.</div>
            </div>
          </div>
          <table className="brut" data-testid="by-person-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="text-right">Inflow</th>
                <th className="text-right">Outflow</th>
                <th className="text-right">Net</th>
                <th className="text-right">#</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {byPerson.map((r) => (
                <tr key={r.name} data-testid={`person-row-${r.name}`}>
                  <td className="font-bold">{r.name}</td>
                  <td className="text-right font-mono-fin num-pos">{inrPlain(r.inflow)}</td>
                  <td className="text-right font-mono-fin num-neg">{inrPlain(Math.abs(r.outflow))}</td>
                  <td className={`text-right font-mono-fin ${r.net < 0 ? "num-neg" : "num-pos"}`}>{inr(r.net)}</td>
                  <td className="text-right font-mono-fin">{r.count}</td>
                  <td>
                    <FormatButtons
                      filter={{ name: r.name }}
                      testidPrefix={`person-dl-${r.name}`}
                    />
                  </td>
                </tr>
              ))}
              {byPerson.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-[#4a5578]">No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="brut-card p-0 overflow-hidden">
          <div className="p-5 border-b-2 border-[#0f1b4c]">
            <div className="brut-label">Bank-wise summary</div>
            <div className="font-display font-black text-2xl">ATM & UPI by Bank</div>
          </div>
          <table className="brut" data-testid="by-bank-table">
            <thead>
              <tr>
                <th>Bank</th>
                <th className="text-right">Total</th>
                <th className="text-right">Entries</th>
              </tr>
            </thead>
            <tbody>
              {byBank.map((r) => (
                <tr key={r.bank}>
                  <td className="font-bold">{r.bank}</td>
                  <td className={`text-right font-mono-fin ${r.total < 0 ? "num-neg" : "num-pos"}`}>{inr(r.total)}</td>
                  <td className="text-right font-mono-fin">{r.count}</td>
                </tr>
              ))}
              {byBank.length === 0 && (
                <tr><td colSpan={3} className="text-center py-6 text-[#4a5578]">No bank-tagged entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* All-transactions quick download */}
      <div className="brut-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="all-download">
        <div>
          <div className="brut-label">Complete ledger</div>
          <div className="font-display font-black text-2xl">All transactions report</div>
          <div className="text-xs text-[#4a5578] mt-1">Download every entry in the office ledger.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadStatement({}, "xlsx")} className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="all-xlsx">
            <FileXls size={16} weight="bold" /> Excel
          </button>
          <button onClick={() => downloadStatement({}, "pdf")} className="brut-btn brut-btn-teal flex items-center gap-2" data-testid="all-pdf">
            <FilePdf size={16} weight="bold" /> PDF
          </button>
          <button onClick={() => downloadStatement({}, "csv")} className="brut-btn brut-btn-secondary flex items-center gap-2" data-testid="all-csv">
            <FileCsv size={16} weight="bold" /> CSV
          </button>
        </div>
      </div>
    </div>
  );
}
